import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Settings, ChevronLeft, ChevronRight, Plus, Trash2, Edit2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const FlashcardViewer = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { tema, subtema, deckName } = useParams();
  const decodedTema = decodeURIComponent(tema);
  const decodedSubtema = decodeURIComponent(subtema);
  const decodedDeckName = decodeURIComponent(deckName);
  
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchCards();
  }, [tema, subtema, deckName]);

  const fetchCards = async () => {
    try {
      const response = await apiClient.get('/flashcards/by-deck', {
        params: {
          specialty: decodedTema,
          topic: decodedSubtema,
          quiz_title: decodedDeckName
        }
      });
      setCards(response.data);
    } catch (error) {
      console.error('Error fetching cards:', error);
      toast.error('Error al cargar las tarjetas');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/flashcards/${encodeURIComponent(decodedTema)}/${encodeURIComponent(decodedSubtema)}`);
  };

  // Play card flip sound when navigating between cards
  const playCardSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_ea98d00c-174c-41bd-9c55-04b64b4359ab/artifacts/m1ljhsvr_flipcard-91468.mp3');
      audio.volume = 0.5;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const nextCard = () => {
    if (currentIndex < cards.length - 1) {
      playCardSound();
      setDirection(1);
      setIsFlipped(false);
      setTimeout(() => setCurrentIndex(currentIndex + 1), 50);
    }
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      playCardSound();
      setDirection(-1);
      setIsFlipped(false);
      setTimeout(() => setCurrentIndex(currentIndex - 1), 50);
    }
  };

  const toggleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleDeleteCard = async (cardId) => {
    try {
      await apiClient.delete(`/flashcards/${cardId}`);
      toast.success('Flashcard eliminada');
      setShowDeleteConfirm(null);
      
      // Update cards list
      const newCards = cards.filter(c => c.id !== cardId);
      setCards(newCards);
      
      // Adjust current index if needed
      if (currentIndex >= newCards.length && newCards.length > 0) {
        setCurrentIndex(newCards.length - 1);
      }
      
      // If no cards left, go back
      if (newCards.length === 0) {
        handleBack();
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('Error al eliminar la flashcard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Volver</span>
            </button>
          </div>
        </header>
        
        <div className="flex-1 flex items-center justify-center pt-16">
          <div className="text-center">
            <p className="text-gray-500 mb-4">No hay tarjetas en este paquete</p>
            <button
              onClick={handleBack}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Volver</span>
          </button>
          
          <div className="text-center">
            <p className="text-sm font-medium text-black">{decodedDeckName}</p>
            <p className="text-xs text-gray-500">{currentIndex + 1} / {cards.length}</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              title="Agregar flashcard"
            >
              <Plus size={14} className="text-black" />
            </button>
            {user.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <Settings size={14} className="text-black" />
              </button>
            )}
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center pt-20 pb-8 px-4">
        <div className="w-full max-w-lg relative">
          {/* Navigation Arrows */}
          <button
            onClick={prevCard}
            disabled={currentIndex === 0}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 z-10 p-2 transition-all ${
              currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'
            }`}
          >
            <ChevronLeft size={32} className="text-gray-500" />
          </button>
          
          <button
            onClick={nextCard}
            disabled={currentIndex === cards.length - 1}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 z-10 p-2 transition-all ${
              currentIndex === cards.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110'
            }`}
          >
            <ChevronRight size={32} className="text-gray-500" />
          </button>

          {/* Card Stack Effect */}
          <div className="relative">
            {/* Background cards for stack effect */}
            {cards.length > 2 && (
              <div className="absolute inset-0 transform translate-y-4 translate-x-2 rotate-2">
                <div className="w-full aspect-[4/3] bg-gray-300 rounded-2xl shadow-sm"></div>
              </div>
            )}
            {cards.length > 1 && (
              <div className="absolute inset-0 transform translate-y-2 translate-x-1 rotate-1">
                <div className="w-full aspect-[4/3] bg-gray-200 rounded-2xl shadow-md"></div>
              </div>
            )}

            {/* Main Card with Flip Animation and Swipe Support */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: direction * 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -100 }}
                transition={{ duration: 0.3 }}
                style={{ perspective: '1000px' }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipeThreshold = 50;
                  const swipeVelocity = 500;
                  
                  if (offset.x < -swipeThreshold || velocity.x < -swipeVelocity) {
                    // Swiped left - go to next card
                    if (currentIndex < cards.length - 1) {
                      playCardSound();
                      setDirection(1);
                      setIsFlipped(false);
                      setTimeout(() => setCurrentIndex(currentIndex + 1), 50);
                    }
                  } else if (offset.x > swipeThreshold || velocity.x > swipeVelocity) {
                    // Swiped right - go to previous card
                    if (currentIndex > 0) {
                      playCardSound();
                      setDirection(-1);
                      setIsFlipped(false);
                      setTimeout(() => setCurrentIndex(currentIndex - 1), 50);
                    }
                  }
                }}
              >
                <div
                  onClick={toggleFlip}
                  className="relative w-full aspect-[4/3] cursor-pointer transition-transform duration-500"
                  style={{ transformStyle: 'preserve-3d', transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
                >
                  {/* Front of Card */}
                  <div
                    className="absolute inset-0 w-full h-full rounded-2xl shadow-xl flex items-center justify-center p-8"
                    style={{ 
                      backfaceVisibility: 'hidden',
                      backgroundColor: 'white'
                    }}
                  >
                    <p className="text-lg sm:text-xl font-medium text-black text-center leading-relaxed">
                      {currentCard.question_text}
                    </p>
                  </div>

                  {/* Back of Card */}
                  <div
                    className="absolute inset-0 w-full h-full rounded-2xl shadow-xl flex items-center justify-center p-8"
                    style={{ 
                      backfaceVisibility: 'hidden',
                      backgroundColor: '#1f2937',
                      transform: 'rotateY(180deg)'
                    }}
                  >
                    <p className="text-lg sm:text-xl font-medium text-white text-center leading-relaxed">
                      {currentCard.answer_text}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Card Info & Actions */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 mb-3">
              Toca para voltear · Desliza para navegar
            </p>
            
            {/* Action buttons */}
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(currentCard.id)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 transition-colors"
              >
                <Trash2 size={14} />
                <span>Eliminar</span>
              </button>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center mt-6 space-x-2">
            {cards.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setIsFlipped(false);
                  setCurrentIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-black w-4' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-black mb-2">¿Eliminar flashcard?</h3>
            <p className="text-gray-600 mb-4">Esta acción no se puede deshacer.</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteCard(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <CreateFlashcardModal
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            fetchCards();
          }}
          defaultSpecialty={decodedTema}
          defaultTopic={decodedSubtema}
          defaultDeck={decodedDeckName}
        />
      )}
    </div>
  );
};

// Inline Create Form Modal
const CreateFlashcardModal = ({ onClose, onSuccess, defaultSpecialty, defaultTopic, defaultDeck }) => {
  const [formData, setFormData] = useState({
    specialty: defaultSpecialty,
    topic: defaultTopic,
    quiz_title: defaultDeck,
    question_text: '',
    answer_text: '',
    personal_notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question_text || !formData.answer_text) {
      toast.error('Por favor completa la pregunta y respuesta');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/flashcards/manual', formData);
      toast.success('Flashcard creada exitosamente');
      onSuccess();
    } catch (error) {
      console.error('Error creating flashcard:', error);
      toast.error('Error al crear la flashcard');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-black">Nueva Flashcard</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            Se agregará a: {defaultDeck}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pregunta *</label>
              <textarea
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                placeholder="Escribe la pregunta..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black resize-none"
                required
              />
            </div>

            {/* Answer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Respuesta *</label>
              <textarea
                value={formData.answer_text}
                onChange={(e) => setFormData({ ...formData, answer_text: e.target.value })}
                placeholder="Escribe la respuesta..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black resize-none"
                required
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas personales</label>
              <textarea
                value={formData.personal_notes}
                onChange={(e) => setFormData({ ...formData, personal_notes: e.target.value })}
                placeholder="Notas adicionales (opcional)"
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-black resize-none"
              />
            </div>

            {/* Buttons */}
            <div className="flex space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FlashcardViewer;
